/* eslint-disable max-len */
/**
 * These hooks re-implement the removed useBlocker and usePrompt hooks from
 * react-router-dom. This implementation was previously owned by ui-components.
 * Source: https://github.com/remix-run/react-router/issues/8139#issuecomment-953816315
 */
import { useCallback, useContext, useEffect } from 'react'
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom'
import type { History, Transition } from 'history'

interface BlockerProps extends Transition {
  retry: () => void
}

export function useBlocker(
  blocker: (props: BlockerProps) => void,
  when = true,
) {
  const navigator = useContext(NavigationContext).navigator as History

  useEffect(() => {
    if (!when) {
      return
    }

    const unblock = navigator.block((tx: Transition) => {
      const autoUnblockingTx = {
        ...tx,
        retry() {
          unblock()
          tx.retry()
        },
      }

      blocker(autoUnblockingTx)
    })

    // eslint-disable-next-line consistent-return
    return unblock
  }, [navigator, blocker, when])
}

export function usePrompt(message: string, when = true) {
  const blocker = useCallback(
    (tx: Transition) => {
      // eslint-disable-next-line no-alert
      if (window.confirm(message)) {
        tx.retry()
      }
    },
    [message],
  )

  useBlocker(blocker, when)
}
